import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector, selectPermissions, selectUser } from '@/app/store';
import { toggleSidebar } from '@/app/slices/uiSlice';
import { toPermissionSet, canAny } from '@/lib/auth/permissions';
import { api } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { NotificationBell } from './NotificationBell';
import { Tooltip } from '@/components/ui/Tooltip';

/**
 * Top bar from the Meridian shell: breadcrumb, global search (⌘K), live
 * notification badge, and a permission-aware quick-create menu. The signed-in
 * user and sign-out live in the sidebar footer (matching the design), so
 * they're not repeated here.
 */

/** First path segment → human breadcrumb label. */
const CRUMBS: Record<string, string> = {
  '': 'Dashboard',
  projects: 'Projects',
  tasks: 'Tasks',
  crm: 'CRM & Leads',
  deals: 'Deals',
  calendar: 'Calendar',
  documents: 'Documents',
  reports: 'Reports',
  ai: 'AI Features',
  gl: 'General Ledger',
  workflow: 'Workflow',
  settings: 'Settings',
  billing: 'Billing',
};

/** Quick-create destinations, gated by the same permission the target page requires. */
const CREATE_ITEMS: { label: string; to: string; permission: string; icon: ReactNode }[] = [
  {
    label: 'Lead',
    to: '/crm/leads/new',
    permission: 'lead:create',
    icon: <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />,
  },
  {
    label: 'Deal',
    to: '/crm/deals/new',
    permission: 'deal:create',
    icon: <path d="M3 3v18h18M7 14l3.5-3.5 3 3L21 7" />,
  },
  {
    label: 'Task',
    to: '/tasks/new',
    permission: 'task:create',
    icon: <><rect x="4" y="4" width="16" height="16" rx="2.5" /><path d="m8.5 12 2.2 2.2 4.5-4.5" /></>,
  },
  {
    label: 'Project',
    to: '/projects/new',
    permission: 'project:create',
    icon: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />,
  },
  {
    label: 'Invoice',
    to: '/finance/invoices/new',
    permission: 'invoice:create',
    icon: <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8ZM14 3v5h5M9 13h6M9 17h6" />,
  },
];

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function Header() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectPermissions);
  const granted = toPermissionSet(permissions);
  const [term, setTerm] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Same cache key the sidebar's tenant switcher uses — this doesn't fire a
  // second request, it just reads the already-fetched workspace name.
  const tenant = useQuery({
    queryKey: ['tenant', 'current'],
    queryFn: () => api.get<{ name: string }>('/api/v1/tenants/current'),
    staleTime: 5 * 60_000,
  });

  const segment = location.pathname.split('/')[1] ?? '';
  const crumb = CRUMBS[segment] ?? (segment ? segment.charAt(0).toUpperCase() + segment.slice(1) : 'Dashboard');

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = term.trim();
    if (q) navigate(`/projects?search=${encodeURIComponent(q)}`);
  };

  // ⌘K / Ctrl+K jumps straight into search from anywhere — skipped while an
  // input/textarea already has focus so it doesn't hijack normal typing.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const createItems = CREATE_ITEMS.filter((item) => canAny(granted, [item.permission]));

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-ink-100 bg-white/90 px-4 backdrop-blur">
      <button
        type="button"
        onClick={() => dispatch(toggleSidebar())}
        className="-ml-1 rounded-lg p-2 text-ink-500 hover:bg-ink-50 lg:hidden"
        aria-label="Open navigation"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="hidden items-center gap-1.5 text-[13px] sm:flex">
        <Link to="/" className="font-medium text-ink-400 transition-colors hover:text-ink-700">
          {tenant.data?.name ?? 'Workspace'}
        </Link>
        <svg className="h-3.5 w-3.5 text-ink-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
        </svg>
        <span className="font-bold text-ink-800">{crumb}</span>
      </nav>

      {/* Global search — searches the project list (the only searchable index today). */}
      <form onSubmit={onSearch} className="relative ml-1 hidden max-w-md flex-1 md:block">
        <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path strokeLinecap="round" d="m21 21-4.3-4.3" />
        </svg>
        <input
          ref={searchRef}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search projects…"
          className="w-full rounded-lg border border-ink-100 bg-ink-50/60 py-2 pl-9 pr-14 text-[13px] text-ink-800 placeholder:text-ink-400 outline-none transition-colors focus:border-primary-300 focus:bg-white"
        />
        <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 rounded border border-ink-200 bg-white px-1.5 py-0.5 font-sans text-[10.5px] font-semibold text-ink-400 sm:flex">
          {isMac ? '⌘' : 'Ctrl'}K
        </kbd>
      </form>

      {/* Pushes notifications/New to the right edge — search caps at max-w-md,
          so without this the trailing controls strand themselves right after it
          on wide viewports instead of hugging the header's right side. */}
      <div className="flex-1" />

      {/* Platform admins get a door to the cross-tenant console. */}
      {user?.isPlatformAdmin && (
        <Link
          to="/platform"
          className="hidden items-center gap-1.5 rounded-md bg-primary-900 px-2.5 py-1.5 text-[11.5px] font-semibold text-brass-400 hover:bg-primary-800 sm:inline-flex"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
          Platform Console
        </Link>
      )}

      {/* Groups the notification + create controls into one visual cluster. */}
      <div className="hidden h-6 w-px bg-ink-100 sm:block" />

      <NotificationBell />

      {createItems.length > 0 && <CreateMenu items={createItems} />}
    </header>
  );
}

/** Quick-create split menu — replaces a single hardcoded "New Project" link
 * with every create action the signed-in user actually has permission for. */
function CreateMenu({ items }: { items: typeof CREATE_ITEMS }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <Tooltip label="Quick create" disabled={open}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="true"
          aria-expanded={open}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 text-[12.5px] font-semibold text-white shadow-card transition-colors hover:bg-primary-700',
            open && 'bg-primary-700',
          )}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path strokeLinecap="round" d="M12 5v14M5 12h14" />
          </svg>
          New
          <svg className={cn('h-3 w-3 shrink-0 transition-transform', open && 'rotate-180')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </Tooltip>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 z-40 mt-1.5 w-48 overflow-hidden rounded-card border border-ink-900/10 bg-white py-1 shadow-popover">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-3 py-2 text-[12.5px] font-medium text-ink-700 transition-colors hover:bg-ink-50 hover:text-ink-900"
              >
                <svg className="h-4 w-4 shrink-0 text-ink-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {item.icon}
                </svg>
                {item.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
